// ExperiencePage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const ExperiencePage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const brandingStats = [
        { number: '88%', label: 'of consumers are less likely to return after a poor brand experience' },
        { number: '3.5x', label: 'higher conversion rates with consistent brand presentation' },
        { number: '70%', label: 'increase in customer trust through professional branding' },
        { number: '40%', label: 'higher engagement with strategic creative design' }
    ];

    const brandingApproaches = [
        {
            icon: 'fas fa-paint-brush',
            title: 'Brand Identity',
            desc: 'Your brand is more than a logo — it\'s how people perceive, remember, and trust you. We craft distinctive and consistent brand identities that stand out in competitive markets and create lasting impressions.'
        },
        {
            icon: 'fas fa-laptop-code',
            title: 'Web Design & Development',
            desc: 'Your website is your most powerful sales asset — it should do more than look good. We design and build high-performance websites and landing pages that combine clean design, seamless UX, and conversion-focused structure.'
        },
        {
            icon: 'fas fa-palette',
            title: 'Creative & Content',
            desc: 'Creative is what stops the scroll — and drives action. We design high-impact creatives tailored for paid media, social platforms, and brand storytelling — built to capture attention and convert.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Conversion Experience (CRO)',
            desc: 'Great design means nothing without performance. We optimise your digital experience to maximise conversions — turning more visitors into leads and customers.'
        }
    ];

    const benefits = [
        ['Strategy-led design — not just aesthetics', 'Every design decision is backed by strategic thinking that drives real business results'],
        ['Built for performance, not just presentation', 'Our solutions are optimised for speed, conversions, and measurable outcomes'],
        ['Fully aligned with your marketing and growth goals', 'Seamless integration with your broader marketing strategy and business objectives'],
        ['Scalable solutions for startups and growing brands', 'Flexible, future-proof designs that grow with your business needs']
    ];

    const targetBusinesses = [
        'Startups launching new brands needing foundational identity, web presence, and market entry strategy',
        'Growing businesses seeking to refresh brand identity, improve website performance, and increase conversion rates',
        'E-commerce brands requiring high-converting product pages, optimised funnels, and compelling creative assets',
        'Marketing teams needing scalable creative production, landing pages, and conversion optimisation support',
        'Established brands undergoing repositioning or digital transformation initiatives'
    ];

    const brandingQuestions = [
        'How can you create a brand that people remember, trust, and choose over competitors?',
        'What improvements to your website would have the greatest impact on conversion rates?',
        'How do you ensure consistent, high-quality creative across all marketing channels?',
        'What technical optimisations best support your performance and growth goals?',
        'How can video and motion content enhance your brand storytelling and engagement?'
    ];

    const brandingTechniques = [
        'Logo Design & Brand Marks',
        'Visual Identity Systems',
        'Typography & Colour Strategy',
        'Brand Guidelines',
        'Brand Positioning Support',
        'Conversion-Focused Websites',
        'Landing Page Design',
        'UX/UI Design',
        'Mobile-Optimised Experiences',
        'Speed & Performance Optimisation',
        'Ad Creatives (Meta, Google, Display)',
        'Social Media Design',
        'Visual Campaign Assets',
        'Video & Motion Graphics',
        'Content Design Systems',
        'Conversion Rate Optimisation (CRO)',
        'A/B Testing',
        'Funnel Design & Optimisation',
        'User Behaviour Analysis',
        'Landing Page Improvements'
    ];

    const serviceComponents = [
        { component: 'Brand Identity', impact: 'Distinctive visual identity, consistent brand recognition, and lasting customer impressions' },
        { component: 'Web Design & Development', impact: 'High-performance websites with clean design, seamless UX, and conversion-focused structure' },
        { component: 'Creative & Content', impact: 'High-impact creatives that stop the scroll, capture attention, and drive action' },
        { component: 'Conversion Experience (CRO)', impact: 'Optimised digital experiences that maximise conversions from visitors to customers' }
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Branding & Creative</span>
                    </h1>
                    <p>We build brands people remember — and experiences that convert.</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In today's crowded digital landscape, a strong brand is no longer optional. It's the foundation of trust, recognition, and growth.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, we don't just design visuals — we create complete brand experiences that connect, engage, and drive measurable results. From identity to websites and high-performing creatives, everything we build is designed with one goal in mind: growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/services/branding"
                                        color='dark'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        Explore Brand Services
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Brand Performance Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {brandingStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Branding & Creative Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Deliver</h2>
                                <p className="sv-section-description">
                                    We build brands people remember — and experiences that convert. Our comprehensive approach combines strategy, design, and performance to create lasting impact.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Brand Questions We Address:</h4>
                                    {brandingQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Techniques Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Our Core Capabilities</h2>
                                <p className="sv-section-description">
                                    We employ sophisticated design methodologies and optimisation strategies to create brand experiences that are not only beautiful but also drive measurable business results.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Complete Service Capabilities:</h4>
                                    <div className="row">
                                        {brandingTechniques.map((technique, index) => (
                                            <div key={index} className="col-md-6">
                                                <div className="sv-question-item">
                                                    <i className="fas fa-cogs"></i>
                                                    {technique}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Branding Matters Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Reachtix?</h2>
                                <p className="sv-section-description">
                                    We don't just create beautiful designs — we build brand experiences that drive growth and deliver measurable results.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Strategy-led design</h5>
                                        <p>Not just aesthetics — every decision is backed by strategic thinking</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-tachometer-alt"></i>
                                        <h5>Built for performance</h5>
                                        <p>Not just presentation — optimised for speed, conversions, and results</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Growth-aligned</h5>
                                        <p>Fully aligned with your marketing and business growth goals</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-expand-alt"></i>
                                        <h5>Scalable solutions</h5>
                                        <p>Flexible designs for startups and growing brands alike</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Brand Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Brand & Creative Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer comprehensive brand solutions that combine creative vision with technical excellence, strategic thinking with practical implementation, and beautiful design with measurable business outcomes.
                                </p>

                                <div className="sv-approaches-grid">
                                    {brandingApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Service Components Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Integrated Brand Service Components</h2>
                                <p className="sv-section-description mb-4">
                                    Our holistic approach ensures every element works together to create cohesive, high-impact brand experiences that drive both user engagement and business results.
                                </p>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Service Component</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {serviceComponents.map((component, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{component.component}</strong>
                                                    </td>
                                                    <td>{component.impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of Brand & Creative Services with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    Strategic brand services transform your digital presence from functional infrastructure to competitive advantage, enabling superior user engagement, improved conversion performance, stronger brand loyalty, and sustainable business growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Brand & Creative Services?</h2>
                                <p className="sv-section-description">
                                    Brand and creative services deliver maximum value for organizations seeking to differentiate through exceptional brand experiences, improve engagement metrics, and drive measurable business outcomes:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're launching a new brand or improving an existing one, we'll help you create a powerful, consistent, and conversion-driven experience.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner With Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Choose Reachtix for Brand & Creative Services?</h2>
                                <p className="sv-section-description">
                                    At Reachtix, we believe exceptional brand experiences are built at the intersection of creativity, strategy, and technology. Our approach combines human-centered design thinking, data-driven optimisation strategies, and measurable business outcomes.
                                </p>
                                <p className="sv-section-description">
                                    We don't just create beautiful visuals — we build brand experiences that solve real business challenges and drive tangible results. Our cross-functional collaboration, agile iterative development, and focus on performance ensure we deliver solutions that are both aesthetically pleasing and commercially effective.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Our Brand Advantages:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-bar"></i>
                                        Strategy-led design — not just aesthetics
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-tachometer-alt"></i>
                                        Built for performance, not just presentation
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-bullseye"></i>
                                        Fully aligned with your marketing and growth goals
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-expand-alt"></i>
                                        Scalable solutions for startups and growing brands
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Let's build something that performs.</h2>
                                <p className="sv-cta-description">
                                    Whether you're launching a new brand or improving an existing one, we'll help you create a powerful, consistent, and conversion-driven experience.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat brand and creative design as a strategic business discipline — not just visual aesthetics. Our specialists blend creative excellence with commercial insight to make your brand a powerful driver of customer trust, loyalty, and sustainable business growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-palette mr-2"></i>
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        Get Free Brand Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ExperiencePage;